import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesService {
  /**
   * Map of confirmation marks. This is implemented like this because it is possible that there
   * exist multiple forms on one page that have a dirty state but are independent of one another.
   * Therefore it is possible to pass a name to the askForConfirmation and the reset method to set
   * a specific confirmation mark. If none is passed then the methods will use a 'global' mark.
   * All dialog components that inherit from the DialogComponent and use a portal-modal-footer will
   * use the 'modal' confirmation mark.
   */
  private needsConfirmation: StringMap<boolean> = {
    global: false,
  };

  private logger = loggerFor('UnsavedChangesService');

  private beforeUnloadHandler = (event: Event) => {
    event.preventDefault();
    event.cancelBubble = true;

    // @ts-ignore
    event.returnValue = '';
  };

  /**
   * True if there exists at least one confirmation mark that is true.
   */
  public get confirm(): boolean {
    return Object.values(this.needsConfirmation).reduce((a, b) => a || b, false);
  }

  private somethingChangedSubject = new Subject<void>();

  constructor(
    private authenticationService: AuthenticationService,
    private dialogService: DialogService,
    private hotkeyService: HotkeyService,
    private translateService: TranslateService
  ) {}

  public canDeactivate(
    component: any,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): Observable<boolean> {
    const canActivate$: Subject<boolean> = new Subject();

    if (this.authenticationService.isAuthenticated === false) {
      this.resetAll();
    }

    if (this.confirm) {
      this.hotkeyService.disable();

      this.openConfirmationDialog().subscribe((navigate: boolean) => {
        this.hotkeyService.enable();

        if (navigate) {
          this.needsConfirmation = {
            global: false,
          };
        }

        canActivate$.next(navigate);
        canActivate$.complete();
      });
    } else {
      setTimeout(() => {
        canActivate$.next(true);
        canActivate$.complete();
      }, 0);
    }

    return canActivate$;
  }

  /**
   * Add a confirmation mark for the given name
   */
  public askForConfirmation(name: string = 'global') {
    this.logger.debug('Ask the user for confirmation upon route change', name);

    this.needsConfirmation[name] = true;
    this.updateBeforeUnloadHandler();
  }

  public openConfirmationDialog(): Observable<boolean> {
    const navigate$ = new Subject<boolean>();

    this.dialogService.openConfirmationDialog({
      cancel: () => {
        navigate$.next(false);
      },
      ok: () => {
        navigate$.next(true);
      },
      text: this.translateService.instant('general.form.unsaved.changes.text'),
      title: this.translateService.instant('general.form.unsaved.changes'),
    });

    return navigate$;
  }

  /**
   * Reset the confirmation mark for the given name
   */
  public reset(name: string = 'global') {
    this.logger.debug('Do not ask the user for confirmation upon route change', name);

    this.needsConfirmation[name] = false;
    this.updateBeforeUnloadHandler();
  }

  /**
   * Reset all confirmation marks
   */
  public resetAll() {
    this.needsConfirmation = {};
    this.updateBeforeUnloadHandler();
  }

  wait(key: string): Observable<void> {
    return new Observable((subscriber) => {
      if (this.needsConfirmation[key] !== true) {
        subscriber.next();
        subscriber.complete();
      } else {
        const subscription = this.somethingChangedSubject.subscribe(() => {
          if (this.needsConfirmation[key] !== true) {
            subscriber.next();
            subscriber.complete();
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      }
    });
  }

  /**
   * Check if there are any confirmation marks and add or remove the beforeunload handler accordingly.
   */
  private updateBeforeUnloadHandler() {
    if (this.confirm) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    this.somethingChangedSubject.next();
  }
}
